// A camera photo processed before it is drawn (Image weights shading, Viewer.setImagePhoto): grey, contrast
// (equalised grey), its edges' strength (Sobel-like: Scharr gradients after a light blur) or its Canny edges (as
// prep/edges.py finds the depth's, thresholds from the photo's own gradients so any exposure works), and the mean
// colour over a coarse grid (Match exposure, exposure.ts). Pure (no DOM): the photo worker and the tests share it.
import { type Gray, blur, grads, grayOf } from './image.ts';

export type PhotoOp = 'rgb' | 'grey' | 'equalize' | 'sobel' | 'canny';
export const PHOTO_OPS: PhotoOp[] = ['rgb', 'grey', 'equalize', 'sobel', 'canny'];
/** The ops whose output is still the photo's brightness (an exposure gain means something). */
export const keepsExposure = (op: PhotoOp) => op === 'rgb' || op === 'grey';

/** The q-quantile (0..1) of v's values (a 1024-bin histogram over 0..max). */
export function quantile(v: Float32Array, q: number): number {
  let max = 0;
  for (let i = 0; i < v.length; i++) if (v[i] > max) max = v[i];
  if (!(max > 0)) return 0;
  const B = 1024, hist = new Uint32Array(B);
  for (let i = 0; i < v.length; i++) hist[Math.min(B - 1, Math.floor((v[i] / max) * B))]++;
  const want = q * v.length;
  let s = 0;
  for (let b = 0; b < B; b++) { s += hist[b]; if (s >= want) return ((b + 1) / B) * max; }
  return max;
}

/** Histogram-equalised grey (0..1). */
export function equalize(g: Gray): Gray {
  const B = 256, hist = new Uint32Array(B), n = g.d.length, d = new Float32Array(n);
  const bin = (v: number) => Math.min(B - 1, Math.max(0, Math.floor(v * B)));
  for (let i = 0; i < n; i++) hist[bin(g.d[i])]++;
  const cdf = new Float32Array(B);
  let s = 0, lo = -1;
  for (let b = 0; b < B; b++) { s += hist[b]; cdf[b] = s; if (lo < 0 && hist[b]) lo = s; }
  const span = Math.max(1, n - lo);
  for (let i = 0; i < n; i++) d[i] = Math.max(0, (cdf[bin(g.d[i])] - lo) / span);
  return { w: g.w, h: g.h, d };
}

/** Gradient strength 0..1: Scharr after a σ 1 blur, scaled so the strongest 1 % saturate. */
export function sobel(g: Gray): Gray {
  const { mag } = grads(blur(g, 1)), top = Math.max(quantile(mag, 0.99), 1e-3), d = new Float32Array(mag.length);
  for (let i = 0; i < mag.length; i++) d[i] = Math.min(1, mag[i] / top);
  return { w: g.w, h: g.h, d };
}

/**
 * Canny edges (1 on an edge, else 0): σ 1.4 blur, gradients, non-maximum suppression across the edge (direction
 * snapped to 4 bins), then hysteresis: strong above the gradients' 90th percentile (at least 0.02 per px, so a
 * blank wall's noise stays out), and weak ones above 0.4 of that where they touch a strong one.
 */
export function canny(g: Gray): Gray {
  const { w, h } = g, { gx, gy, mag } = grads(blur(g, 1.4)), n = w * h;
  const hi = Math.max(quantile(mag, 0.9), 0.02), lo = 0.4 * hi;
  const nms = new Float32Array(n);
  for (let y = 1; y < h - 1; y++) for (let x = 1; x < w - 1; x++) {
    const i = y * w + x, m = mag[i];
    if (m < lo) continue;
    // the neighbours across the edge (along the gradient)
    const a = ((Math.atan2(gy[i], gx[i]) * 180) / Math.PI + 180) % 180;
    const o = a < 22.5 || a >= 157.5 ? 1 : a < 67.5 ? w + 1 : a < 112.5 ? w : w - 1;
    if (m >= mag[i - o] && m >= mag[i + o]) nms[i] = m;
  }
  const out = new Float32Array(n), stack: number[] = [];
  for (let i = 0; i < n; i++) if (nms[i] >= hi && !out[i]) {
    out[i] = 1; stack.push(i);
    while (stack.length) {
      const j = stack.pop()!, x = j % w, y = (j / w) | 0;
      for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
        const xx = x + dx, yy = y + dy, k = yy * w + xx;
        if (xx < 0 || yy < 0 || xx >= w || yy >= h || out[k] || nms[k] < lo) continue;
        out[k] = 1; stack.push(k);
      }
    }
  }
  return { w, h, d: out };
}

/** The photo (RGBA bytes, w × h) after op, as RGBA bytes (rgb: the same array). */
export function filterPhoto(rgba: Uint8ClampedArray, w: number, h: number, op: PhotoOp): Uint8ClampedArray {
  if (op === 'rgb') return rgba;
  const g = grayOf(rgba, w, h), r = op === 'grey' ? g : op === 'equalize' ? equalize(g) : op === 'sobel' ? sobel(g) : canny(g);
  const out = new Uint8ClampedArray(4 * w * h);
  for (let i = 0; i < w * h; i++) { const v = Math.round(r.d[i] * 255); out[4 * i] = out[4 * i + 1] = out[4 * i + 2] = v; out[4 * i + 3] = 255; }
  return out;
}

/** The photo's mean colour (RGB bytes) over a gw × gh grid of cells (row-major, 3 per cell). */
export function colourGrid(rgba: Uint8ClampedArray, w: number, h: number, gw: number, gh: number): Uint8Array {
  const sum = new Float64Array(3 * gw * gh), cnt = new Uint32Array(gw * gh), out = new Uint8Array(3 * gw * gh);
  for (let y = 0; y < h; y++) {
    const cy = Math.min(gh - 1, Math.floor((y * gh) / h));
    for (let x = 0; x < w; x++) {
      const c = cy * gw + Math.min(gw - 1, Math.floor((x * gw) / w)), p = 4 * (y * w + x);
      sum[3 * c] += rgba[p]; sum[3 * c + 1] += rgba[p + 1]; sum[3 * c + 2] += rgba[p + 2]; cnt[c]++;
    }
  }
  for (let c = 0; c < gw * gh; c++) if (cnt[c]) for (let k = 0; k < 3; k++) out[3 * c + k] = Math.round(sum[3 * c + k] / cnt[c]);
  return out;
}
